import {
  EyeOutlined,
  HeartOutlined,
  LikeOutlined,
  MessageOutlined,
  ShareAltOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import { Tooltip } from 'antd'
import cls from 'classnames'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { UserAvatarGroup, UserListModal } from '@/components'
import { RequestedPostFragment, RequestedRecommendPostInfoFragment } from '@/generated/graphql'
import { useLayoutSize } from '@/hooks/layout-size.hooks'
import { EventActions } from '@/modules/calendar/event-card/event-card-actions.component'
import { ContentModeContext } from '@/modules/user/user-timeline/user-timeline-context'
import { routes } from '@/routes'

import s from '../post-card.module.less'

interface PostPerformanceProps {
  post: RequestedPostFragment & { recommendPostInfo?: RequestedRecommendPostInfoFragment }
  isSinglePost?: boolean
  showReadCount?: boolean
  onClickComment: () => void
}

export const PostPerformance: React.FC<PostPerformanceProps> = ({ post, showReadCount = false, onClickComment }) => {
  const { t } = useTranslation()
  const avatarMaxCount = 9

  const { isMobileSize } = useLayoutSize()

  const mode = React.useContext(ContentModeContext)

  if (post.archivedAt) {
    return null
  }

  const participants = post.event && (
    <Tooltip title={`${post.event.participatedCount}` + t('calendar.participantMes')}>
      <span className={s.extraAction}>
        <UserAddOutlined /> {post.event.participatedCount}
        {post.event.openPosition && `/${post.event.openPosition}`}
      </span>
    </Tooltip>
  )
  const participatedCount =
    post.event &&
    (post.event.participatedCount > 0 ? (
      <UserListModal
        postId={post.postId}
        title={t('calendar.participant')}
        type='eventParticipants'
        eventId={post.event.eventId}
        paginationInput={{ sortBy: 'updatedAt', useFirst: true }}
        showSearch={true}
        showIndex={true}
      >
        {participants}
      </UserListModal>
    ) : (post.event?.openPosition || 0) > 0 ? (
      participants
    ) : undefined)

  const interestedCount = post.event && post.event.interestedCount > 0 && (
    <UserListModal
      postId={post.postId}
      title={t('calendar.interestedUsers')}
      type='eventInterestedUsers'
      eventId={post.event.eventId}
      showSearch={true}
    >
      <Tooltip title={`${post.event.interestedCount}` + t('calendar.interestedUserMes')}>
        <span className={s.extraAction}>
          <HeartOutlined /> {post.event.interestedCount}
        </span>
      </Tooltip>
    </UserListModal>
  )

  const readCount = post.readCount > 0 && (post.isViewerCreated || showReadCount) && (
    <UserListModal postId={post.postId} title={t('post.readUser')} type='readUsers' showSearch={false}>
      <Tooltip title={`${post.readCount}` + t('post.readUserMes')}>
        <span className={s.extraAction}>
          <EyeOutlined /> {post.readCount}
        </span>
      </Tooltip>
    </UserListModal>
  )

  const likedUsers = post.likedUsers.edges
    .map(({ node }) => node)
    .slice()
    .reverse()

  const likedAvatars = post.likedCount > 0 && (
    <span className={s.extraAction}>
      <LikeOutlined style={{ marginRight: 6 }} />
      <UserAvatarGroup
        users={likedUsers}
        totalCount={post.likedCount}
        showCount={avatarMaxCount}
        postId={post.postId}
        title={t('post.likeUser')}
        avatarTooltipMessage={t('post.likeUserMes', {
          countText: post.likedCount - avatarMaxCount + 1,
        })}
        type='likedUsers'
        showSearch={false}
      />
    </span>
  )

  const commentsCount = post.commentCount > 0 && (
    <Tooltip title={t('post.commentExpand') + ` ${post.commentCount} ` + t('post.commentUserMes')}>
      <span onClick={() => onClickComment()} className={cls(s.extraAction, s.like)}>
        <MessageOutlined /> {post.commentCount}
      </span>
    </Tooltip>
  )

  const shareCount = post.sharedCount > 0 && (
    <UserListModal postId={post.postId} title={t('post.sharedUser')} type='sharedUsers' showSearch={false}>
      <Tooltip title={`${post.sharedCount} 次分享`}>
        <span className={s.extraAction}>
          <ShareAltOutlined /> {post.sharedCount}
        </span>
      </Tooltip>
    </UserListModal>
  )

  const eventActions = !mode.isSimplified && post.event && (
    <div className={s.actions}>
      <EventActions hasDivider={false} data={post.event} />
    </div>
  )

  let recommendPostInfo = null

  if (post.recommendPostInfo) {
    const isCommentFollowerMore =
      post.recommendPostInfo.commentFolloweeIds.length > post.recommendPostInfo.likedFolloweeIds.length

    const first = isCommentFollowerMore
      ? post.recommendPostInfo.commentFollowees[0]
      : post.recommendPostInfo.likedFollowees[0]

    if (first) {
      const recommendCount = isCommentFollowerMore
        ? post.recommendPostInfo.commentFollowees.length
        : post.recommendPostInfo.likedFollowees.length

      recommendPostInfo = (
        <p>
          <Trans
            i18nKey={`post.recommend.${isCommentFollowerMore ? 'commentInfo' : 'likedInfo'}${
              recommendCount - 1 > 0 ? '_more' : ''
            }`}
            values={{ name: first.name, count: recommendCount - 1 }}
            components={{ 1: <a href={routes.users.page(first.userId)} /> }}
          />
        </p>
      )
    }
  }

  return (
    <div>
      <div className={s.recommend}>{!!eventActions && !isMobileSize && recommendPostInfo}</div>
      <div className={s.extrasContainer}>
        <div className={s.extrasActions}>
          {participatedCount}
          {interestedCount}
          {readCount}
          {commentsCount}
          {shareCount}
          {likedAvatars}
        </div>
        {eventActions}
        {!eventActions && !isMobileSize && recommendPostInfo}
      </div>
    </div>
  )
}
